import { Briefcase, ExternalLink, Linkedin, MapPin } from 'lucide-react'
import Image from 'next/image'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { linkedinGetCurrentMemberProfile } from 'sprites-types/workflow/tools/linkedinGetCurrentMemberProfile'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function LinkedinGetCurrentMemberProfileDisplayComponent({
  nodeExecution,
}: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(linkedinGetCurrentMemberProfile, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  const profile = result
  const firstName = (profile as any)?.localizedFirstName || (profile as any)?.firstName || 'Unknown'
  const lastName = (profile as any)?.localizedLastName || (profile as any)?.lastName || ''
  const fullName = `${firstName} ${lastName}`.trim()
  const headline = (profile as any)?.localizedHeadline || (profile as any)?.headline
  const location = (profile as any)?.geoLocationName || (profile as any)?.location
  const industry = (profile as any)?.localizedIndustryName || (profile as any)?.industry
  const profilePicture = (profile as any)?.profilePicture?.displayImage
  const profileId = (profile as any)?.id
  const profileUrl = profileId ? `https://linkedin.com/in/${profileId}` : null

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="flex items-start gap-4 px-4 py-4">
        <div className="flex-shrink-0">
          {profilePicture ? (
            <Image
              src={profilePicture}
              alt={`${fullName} profile`}
              width={64}
              height={64}
              className="h-16 w-16 rounded-full border-2 border-blue-200 object-cover"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-blue-700">
              <Linkedin className="h-8 w-8 text-white" />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2">
            <div className="text-sm font-bold text-gray-900">{fullName}</div>
          </div>

          {headline && <div className="mb-2 text-sm text-gray-700">{headline}</div>}

          <div className="space-y-1 text-xs text-gray-600">
            {location && (
              <div className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                <span>{location}</span>
              </div>
            )}
            {industry && (
              <div className="flex items-center gap-1">
                <Briefcase className="h-3 w-3" />
                <span>{industry}</span>
              </div>
            )}
          </div>

          {profileUrl && (
            <div className="mt-3">
              <a
                href={profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <ExternalLink className="h-3 w-3" />
                View LinkedIn Profile
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function downloadLinkedinCurrentProfile(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function linkedinGetCurrentMemberProfileToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(linkedinGetCurrentMemberProfile, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data

  const profile = result
  const firstName = (profile as any)?.localizedFirstName || (profile as any)?.firstName || 'Unknown'
  const lastName = (profile as any)?.localizedLastName || (profile as any)?.lastName || ''
  const fullName = `${firstName} ${lastName}`.trim()
  const headline = (profile as any)?.localizedHeadline || (profile as any)?.headline
  const location = (profile as any)?.geoLocationName || (profile as any)?.location
  const industry = (profile as any)?.localizedIndustryName || (profile as any)?.industry

  return `## LinkedIn Profile: ${fullName}

${headline ? `**Headline:** ${headline}` : ''}
${location ? `**Location:** ${location}` : ''}
${industry ? `**Industry:** ${industry}` : ''}

---
`
}

export const LinkedinGetCurrentMemberProfileDisplay = createWorkflowToolComponent(
  LinkedinGetCurrentMemberProfileDisplayComponent,
  downloadLinkedinCurrentProfile,
  linkedinGetCurrentMemberProfileToMarkdown
)
